import type {
  EngineAdapter,
  Layer,
  LayerKind,
  ViewerCamera,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import { declareCapabilities, type EngineCapabilities } from '@/engines/contract';

const THREE_SUPPORTED: LayerKind[] = ['bim'];

const THREE_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'three',
  supportedLayerKinds: THREE_SUPPORTED,
  supportsTime: false,
  supportsTerrain: false,
  supports3DTiles: 'none',
  supportsInterleaving: ['cesium'],
  defenceVariantSafe: true,
  pluginHostKinds: ['measurement', 'annotation', 'model-tree'],
});

interface ThreeLayerRecord {
  layerId: string;
  group: unknown;
  nodeNames: string[];
  vertexCount: number;
  faceCount: number;
}

// Lazy-loaded Three.js modules (resolved only when the adapter mounts).
let _threePromise: Promise<typeof import('three')> | null = null;
function loadThree() {
  if (!_threePromise) _threePromise = import('three');
  return _threePromise;
}

/**
 * Three.js adapter for GLB/BIM model rendering.
 *
 * All Three.js imports are dynamic and deferred to mount() time so that
 * importing this file does not trigger Vite to resolve the heavy 'three'
 * package during initial page load.
 */
export class ThreeAdapter implements EngineAdapter {
  id = 'three' as const;
  readonly capabilities: EngineCapabilities = THREE_CAPABILITIES;

  private mounted = false;
  private host: ViewerHostContext | null = null;
  private layers = new Map<string, ThreeLayerRecord>();
  private selectedObjectId: string | null = null;

  private scene: any = null;
  private camera: any = null;
  private renderer: any = null;
  private controls: any = null;
  private animFrameId: number | null = null;
  private THREE: any = null;

  supports(kind: LayerKind): boolean {
    return THREE_SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    this.host = host;

    const THREE = await loadThree();
    this.THREE = THREE;

    const container = host.container as HTMLDivElement;
    const width = container.clientWidth || 800;
    const height = container.clientHeight || 600;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a2e);

    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 100000);
    this.camera.position.set(50, 50, 50);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    container.appendChild(this.renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(100, 200, 100);
    dirLight.castShadow = true;
    this.scene.add(dirLight);

    try {
      const { OrbitControls } = await import(
        'three/examples/jsm/controls/OrbitControls.js'
      );
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.1;
    } catch {
      // OrbitControls may not be available; continue without controls.
    }

    const animate = () => {
      this.animFrameId = requestAnimationFrame(animate);
      this.controls?.update?.();
      this.renderer.render(this.scene, this.camera);
    };
    animate();

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0) {
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h);
      }
    };
    window.addEventListener('resize', onResize);
    (this as any)._resizeHandler = onResize;

    this.mounted = true;
  }

  async unmount(): Promise<void> {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.controls?.dispose?.();
    if (this.renderer) {
      this.renderer.domElement?.remove?.();
      this.renderer.dispose?.();
    }
    if ((this as any)._resizeHandler) {
      window.removeEventListener('resize', (this as any)._resizeHandler);
    }
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.layers.clear();
    this.mounted = false;
    this.host = null;
    this.selectedObjectId = null;
  }

  async addLayer(layer: Layer): Promise<void> {
    if (!this.mounted || !this.scene || !this.THREE) return;
    if (layer.kind !== 'bim') return;

    const modelUrl = layer.endpoints.model;
    if (!modelUrl) {
      this.host?.emit({
        type: 'error',
        message: `[three:${layer.id}] bim layer has no endpoints.model URL`,
      });
      return;
    }

    try {
      const { GLTFLoader } = await import(
        'three/examples/jsm/loaders/GLTFLoader.js'
      );
      const loader = new GLTFLoader();
      const gltf: any = await new Promise((resolve, reject) => {
        loader.load(modelUrl, resolve, undefined, reject);
      });

      const group = gltf.scene;
      this.scene.add(group);

      const nodeNames: string[] = [];
      let vertexCount = 0;
      let faceCount = 0;
      group.traverse((child: any) => {
        if (child.name) nodeNames.push(child.name);
        if (child.isMesh && child.geometry) {
          const pos = child.geometry.attributes?.position;
          if (pos) vertexCount += pos.count;
          const idx = child.geometry.index;
          if (idx) faceCount += idx.count / 3;
          else if (pos) faceCount += pos.count / 3;
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      this.layers.set(layer.id, { layerId: layer.id, group, nodeNames, vertexCount, faceCount });

      this.fitCameraToObject(group);
    } catch (err) {
      this.host?.emit({
        type: 'error',
        message: `[three:${layer.id}] GLB load failed: ${err}`,
      });
    }
  }

  async removeLayer(layerId: string): Promise<void> {
    const record = this.layers.get(layerId);
    if (record?.group && this.scene) {
      this.scene.remove(record.group);
    }
    this.layers.delete(layerId);
    if (this.selectedObjectId?.startsWith(`${layerId}:`)) {
      this.selectedObjectId = null;
    }
  }

  flyTo(camera: ViewerCamera): void {
    if (!this.camera) return;
    const [lon, lat] = camera.lonLat;
    const height = camera.height ?? 100;
    this.camera.position.set(lon, height, lat);
    this.camera.lookAt(lon, 0, lat);
    this.controls?.target?.set(lon, 0, lat);
  }

  setSelectedObjectId(objectId: string | null): void {
    this.selectedObjectId = objectId;
  }

  getModelInfo(layerId: string): {
    vertexCount: number;
    faceCount: number;
    nodeNames: string[];
  } | null {
    const record = this.layers.get(layerId);
    if (!record) return null;
    return {
      vertexCount: record.vertexCount,
      faceCount: record.faceCount,
      nodeNames: record.nodeNames,
    };
  }

  setLayerOpacity(layerId: string, opacity: number): void {
    const record = this.layers.get(layerId);
    if (!record?.group) return;
    (record.group as any).traverse?.((child: any) => {
      if (child.isMesh && child.material) {
        child.material.transparent = opacity < 1;
        child.material.opacity = opacity;
      }
    });
  }

  getPlaceholderState(): {
    status: 'idle' | 'ready';
    bimLayerIds: string[];
    selectedObjectId: string | null;
  } {
    return {
      status: this.mounted ? 'ready' : 'idle',
      bimLayerIds: [...this.layers.keys()],
      selectedObjectId: this.selectedObjectId,
    };
  }

  private fitCameraToObject(object: any): void {
    if (!this.camera || !this.THREE) return;
    const box = new this.THREE.Box3().setFromObject(object);
    const center = box.getCenter(new this.THREE.Vector3());
    const size = box.getSize(new this.THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const distance = maxDim * 2;

    this.camera.position.set(
      center.x + distance * 0.6,
      center.y + distance * 0.8,
      center.z + distance * 0.6,
    );
    this.camera.lookAt(center);
    if (this.controls) {
      this.controls.target.copy(center);
      this.controls.update();
    }
  }
}
